<template>
    <div>
        <froala :class="{invalid: isInvalid}" :id="id" :name="name" :required="_required" tag="textarea" :config="editorConfig" v-model:value="input"
            v-bind="ariaValidationAttributes" ref="editor"></froala>
        <textarea v-if="!initialized" v-show="false" :name="name" v-model="input"></textarea>
    </div>
</template>

<script setup lang="ts">
    import {useAbstractFormInput} from '@/composables/abstractFormInput';
    import {usePortuna} from '@/composables/portuna';
    import {FormRowKey} from '@/library/form';
    import {FroalaDefaultConfig} from '@/library/froala';
    import FroalaEditor, {type FroalaOptions} from 'froala-editor';
    import {ref, computed, onUnmounted, watch, inject, toRefs} from 'vue';

    const portuna = usePortuna();

    const emit = defineEmits<{
        (eventName: 'update:modelValue', modelValue: string): void;
        (eventName: 'instance-ready', editor: FroalaEditor): void;
        (eventName: 'initialized'): void;
    }>();

    const props = withDefaults(
        defineProps<{
            name?: string;
            required?: boolean;
            overwriteRequired?: boolean;
            errorName?: string;
            filterName?: string;
            modelValue?: string;
            id: string;
            config?: Partial<FroalaOptions>;
        }>(),
        {
            required: false,
            overwriteRequired: false,
            errorName: '',
            filterName: '',
            modelValue: '',
        },
    );

    const parentFormRow = inject<Nullable<IFormRowContext>>(FormRowKey, null);

    const {
              name,
              required,
              overwriteRequired,
              errorName,
              filterName,
              modelValue,
              id,
              config,
          } = toRefs(props);

    const {
              _required,
              ariaValidationAttributes,
              validateValue,
              isInvalid,
              expose,
          } = useAbstractFormInput(
        parentFormRow,
        id,
        required,
        overwriteRequired,
        errorName,
        filterName,
    );

    const editor = ref<{ getEditor: () => FroalaEditor; createEditor: Function; destroyEditor: Function; }>();

    const input         = ref('');
    const checkInterval = ref<Nullable<number>>(null);
    const initialized   = ref<boolean>(false);

    const editorConfig = computed(() =>
    {
        const options: Partial<FroalaOptions> = {
            initializationCallbacks: [
                (editor: FroalaEditor) =>
                {
                    emit('instance-ready', editor);
                },
            ],
        };

        if (!config.value)
        {
            return Object.assign({}, FroalaDefaultConfig, options);
        }

        return Object.assign({}, FroalaDefaultConfig, options, config.value);
    });

    const clearCheckInterval = () =>
    {
        checkInterval.value && window.clearInterval(checkInterval.value);
    };

    const watchInitialization = () =>
    {
        return new Promise<void>((resolve) =>
        {
            checkInterval.value = window.setInterval(() =>
            {
                if (editor.value && editor.value.getEditor() && editor.value.getEditor().html)
                {
                    clearCheckInterval();

                    resolve();
                }
            }, 100);
        });
    };

    const getEditor = () =>
    {
        return editor.value!.getEditor();
    };

    const updateValue = () =>
    {
        return validateValue(
            input.value,
        );
    };

    const loadEditor = () =>
    {
        editor.value!.createEditor();
    };

    const unloadEditor = () =>
    {
        editor.value!.destroyEditor();
    };

    input.value = modelValue.value;

    watchInitialization().then(() =>
    {
        initialized.value = true;

        portuna.emitter.emit('initialized');
        emit('initialized');
    });

    onUnmounted(() =>
    {
        clearCheckInterval();
    });

    watch(input, (value: string) =>
    {
        emit('update:modelValue', value);
    });

    watch(modelValue, () =>
    {
        input.value = modelValue.value;
    });

    defineExpose(Object.assign({}, portuna, expose, {
        componentName: 'FormTextEditor',
        unloadEditor,
        loadEditor,
        updateValue,
        getEditor,
        editorConfig,
        input,
        initialized,
    }));
</script>